import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, MapPin, Phone } from "lucide-react";
import api from "../lib/api";
import { FadeUp, Counter } from "../components/Motion";
import LeadForm from "../components/LeadForm";
import { FAQSchema, ServiceSchema, BreadcrumbSchema } from "../components/SeoSchema";
import PageMeta from "../components/PageMeta";

const buildFaqs = (serviceName, areaName) => [
  {
    q: `Do you provide ${serviceName.toLowerCase()} in ${areaName}?`,
    a: `Yes — ${serviceName.toLowerCase()} is one of our core builds in ${areaName}. We've completed projects across the city and surrounding neighbourhoods, all engineered for Alberta's freeze-thaw cycle.`,
  },
  {
    q: `How much does ${serviceName.toLowerCase()} cost in ${areaName}?`,
    a: `Budgets depend on scope, materials, and site conditions. Use our online Estimator for a transparent range, or book a free consultation and we'll deliver a fixed proposal within five business days.`,
  },
  {
    q: `How long does a ${serviceName.toLowerCase()} project take?`,
    a: `Most ${areaName}-area ${serviceName.toLowerCase()} projects run 2–6 weeks from build start. Booking lead time during peak season (May–September) is typically 4–8 weeks.`,
  },
  {
    q: `Are you licensed and insured to work in ${areaName}?`,
    a: `Yes. NorthCrest is fully licensed and insured across Alberta — WCB coverage, $5M general liability, and certified hardscape and irrigation installers on every crew.`,
  },
];

export default function AreaService() {
  const { areaSlug, serviceSlug } = useParams();
  const nav = useNavigate();
  const [area, setArea] = useState(null);
  const [service, setService] = useState(null);
  const [projects, setProjects] = useState([]);
  const [otherAreas, setOtherAreas] = useState([]);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/service-areas/${areaSlug}`).catch(() => null),
      api.get(`/services/${serviceSlug}`).catch(() => null),
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/service-areas").catch(() => ({ data: [] })),
      api.get("/services").catch(() => ({ data: [] })),
    ]).then(([a, s, p, aa, ss]) => {
      if (!a?.data || !s?.data) {
        nav("/areas", { replace: true });
        return;
      }
      setArea(a.data);
      setService(s.data);
      setProjects(p.data);
      setOtherAreas((aa.data || []).filter((x) => x.slug !== areaSlug));
      setOtherServices((ss.data || []).filter((x) => x.slug !== serviceSlug && x.category === s.data.category));
      setLoading(false);
    });
  }, [areaSlug, serviceSlug, nav]);

  if (loading || !area || !service) {
    return <div className="pt-32 px-6 text-[#D8D6D0]/40">Loading…</div>;
  }

  const localProjects = projects.filter((p) => (p.location || "").toLowerCase() === area.name.toLowerCase());
  const faqs = buildFaqs(service.name, area.name);
  const pageTitle = `${service.name} in ${area.name} — NorthCrest Landscaping`;
  const pageDescription = `Premium ${service.name.toLowerCase()} in ${area.name}, Alberta. Architect-led design, engineered builds, and full warranty. ${area.projects_completed}+ ${area.name} projects delivered by NorthCrest.`;

  return (
    <div data-testid="area-service-page" className="pt-32 pb-32">
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        path={`/areas/${areaSlug}/${serviceSlug}`}
        image={service.image_url}
      />
      <ServiceSchema
        serviceName={service.name}
        areaName={area.name}
        description={pageDescription}
        imageUrl={service.image_url}
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://northcrestlandscaping.ca/" },
          { name: "Service Areas", url: "https://northcrestlandscaping.ca/areas" },
          { name: area.name, url: `https://northcrestlandscaping.ca/areas/${areaSlug}` },
          { name: service.name, url: `https://northcrestlandscaping.ca/areas/${areaSlug}/${serviceSlug}` },
        ]}
      />

      {/* Breadcrumb */}
      <section className="px-6 md:px-12 lg:px-16 mb-8">
        <FadeUp>
          <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-widest text-[#D8D6D0]/50">
            <Link to="/areas" className="hover:text-[#F5F3EF] link-underline" data-testid="breadcrumb-areas">Areas</Link>
            <span>/</span>
            <Link to={`/areas/${areaSlug}`} className="hover:text-[#F5F3EF] link-underline" data-testid="breadcrumb-area">{area.name}</Link>
            <span>/</span>
            <span className="text-[#8C1D30]">{service.name}</span>
          </div>
        </FadeUp>
      </section>

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-16 mb-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeUp className="lg:col-span-7">
          <div className="overline text-[#8C1D30] mb-4">{service.name} · {area.name}</div>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {service.name} in <span className="italic text-[#D8D6D0]">{area.name}.</span>
          </h1>
          <p className="text-[#D8D6D0]/70 mt-8 text-lg leading-relaxed max-w-2xl">
            {service.long_description}
          </p>
          <p className="text-[#D8D6D0]/60 mt-4 text-base leading-relaxed max-w-2xl">
            Architect-led design and engineered builds for {area.name}'s most discerning homeowners and commercial property owners. Every project is hand-crafted by our crews and warrantied end-to-end.
          </p>
          <p className="text-[#D8D6D0]/55 mt-4 text-base leading-relaxed max-w-2xl">
            {area.name} sits in zone 3b of Alberta's growing map — meaning {service.name.toLowerCase()} projects here must be engineered for −40 °C winters, summer chinooks, and heavy spring runoff. NorthCrest specifies a 6&quot; engineered base, frost-protected footings, and freeze-thaw-rated materials on every build in the {area.name} area. Local soil tends to be heavy clay over till, which dictates our drainage approach and base preparation depth.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/estimate"
              data-testid="area-service-cta-estimate"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300"
            >
              Get Free Estimate <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+17806557604"
              data-testid="area-service-cta-call"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-wider uppercase transition-all duration-300"
            >
              <Phone className="w-4 h-4" /> (780) 655-7604
            </a>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-5">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={service.image_url} alt={`${service.name} in ${area.name}`} className="w-full h-full object-cover" />
          </div>
        </FadeUp>
      </section>

      {/* TRUST STRIP */}
      <section className="px-6 md:px-12 lg:px-16 py-16 border-y border-[#D8D6D0]/10 grid grid-cols-2 md:grid-cols-4 gap-10">
        <FadeUp>
          <div className="font-serif-display text-5xl"><Counter value={area.projects_completed} suffix="+" /></div>
          <div className="overline text-[#D8D6D0]/50 mt-3">{area.name} Projects</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <div className="font-serif-display text-5xl">6+</div>
          <div className="overline text-[#D8D6D0]/50 mt-3">Years Building</div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="font-serif-display text-5xl">100%</div>
          <div className="overline text-[#D8D6D0]/50 mt-3">Licensed & Insured</div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="font-serif-display text-5xl">4.9★</div>
          <div className="overline text-[#D8D6D0]/50 mt-3">Google Reviews</div>
        </FadeUp>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-5">
          <div className="overline text-[#8C1D30] mb-4">What's included</div>
          <h2 className="font-serif-display text-4xl md:text-5xl">Our {service.name.toLowerCase()} process in {area.name}.</h2>
          <p className="text-[#D8D6D0]/65 mt-6 max-w-md">
            A fully transparent, end-to-end process — from concept to final walk-through.
          </p>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-7">
          <ul className="space-y-4">
            {[
              "On-site consultation & precise measurement",
              "Architectural design + 3D visualization",
              "Engineered base systems for Alberta freeze-thaw",
              "Premium materials sourced from European suppliers",
              "In-house crews — no sub-contractors on hardscape",
              "Final walk-through, photographs, and full warranty",
            ].map((s, i) => (
              <li key={s} className="flex items-start gap-3 text-[#D8D6D0]/85">
                <CheckCircle2 className="w-5 h-5 text-[#8C1D30] flex-shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
      </section>

      {/* RELEVANT PROJECTS */}
      {localProjects.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-4">Local work</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mb-12">Recent projects in {area.name}.</h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {localProjects.slice(0, 6).map((p) => (
              <FadeUp key={p.id}>
                <div className="group overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  </div>
                  <div className="mt-4">
                    <div className="font-serif-display text-xl">{p.title}</div>
                    <div className="overline text-[#D8D6D0]/50 mt-1">{p.service_type}</div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-3">FAQ</div>
            <h2 className="font-serif-display text-4xl md:text-5xl">{service.name} in {area.name}.</h2>
          </FadeUp>
          <div className="lg:col-span-2 divide-y divide-[#D8D6D0]/10">
            {faqs.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.05}>
                <details className="group py-6" data-testid={`areaservice-faq-${i}`}>
                  <summary className="cursor-pointer list-none flex justify-between items-start gap-6">
                    <span className="font-serif-display text-xl md:text-2xl text-[#F5F3EF]">{f.q}</span>
                    <span className="overline text-[#D8D6D0]/40 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-[#D8D6D0]/70 max-w-3xl">{f.a}</p>
                </details>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <FadeUp>
            <h2 className="font-serif-display text-4xl md:text-5xl">Start your {area.name} {service.name.toLowerCase()} project.</h2>
            <p className="text-[#D8D6D0]/60 mt-6 max-w-md">A senior designer will respond within one business day.</p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <LeadForm leadType="residential" source={`area-${areaSlug}-${serviceSlug}`} compact />
          </FadeUp>
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="px-6 md:px-12 lg:px-16 py-16 border-t border-[#D8D6D0]/10 grid grid-cols-1 md:grid-cols-2 gap-12">
        {otherServices.length > 0 && (
          <FadeUp>
            <div className="overline text-[#D8D6D0]/50 mb-5">Other services in {area.name}</div>
            <div className="flex flex-wrap gap-3">
              {otherServices.slice(0, 8).map((s) => (
                <Link
                  key={s.slug}
                  to={`/areas/${areaSlug}/${s.slug}`}
                  data-testid={`xlink-service-${s.slug}`}
                  className="inline-flex items-center px-4 py-2.5 border border-[#D8D6D0]/15 hover:border-[#F5F3EF]/40 hover:bg-[#F5F3EF]/5 transition-colors text-sm"
                >
                  {s.name} in {area.name}
                </Link>
              ))}
            </div>
          </FadeUp>
        )}
        {otherAreas.length > 0 && (
          <FadeUp delay={0.1}>
            <div className="overline text-[#D8D6D0]/50 mb-5">{service.name} in other areas</div>
            <div className="flex flex-wrap gap-3">
              {otherAreas.slice(0, 8).map((a) => (
                <Link
                  key={a.slug}
                  to={`/areas/${a.slug}/${serviceSlug}`}
                  data-testid={`xlink-area-${a.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D8D6D0]/15 hover:border-[#F5F3EF]/40 hover:bg-[#F5F3EF]/5 transition-colors text-sm"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#8C1D30]" />
                  {service.name} in {a.name}
                </Link>
              ))}
            </div>
          </FadeUp>
        )}
      </section>
    </div>
  );
}
